/**
 * A report sent from the app to the account area (Codex 1022): a bug or an
 * improvement, the context the person left ticked and the images they kept.
 *
 * One multipart POST to `FEEDBACK_PATH`: the field `request` holds this JSON,
 * each `image` field one picture. The ceilings and the validator live here
 * because both sides hold them (1020/one-validator-both-sides): the app says a
 * refusal on the form before anything leaves, and the Worker, which trusts no
 * caller, holds them again.
 */

/** Under the account area's API root, which each side names its own way. */
export const FEEDBACK_ROUTE = '/feedback';

/**
 * A report's one way through (Codex 1022): every report is received `pending`,
 * a system administrator passes it `ready`, a workflow takes it `in_progress` and
 * closes it. It can be rejected from anywhere it is still open, and it never goes back.
 */
export const FEEDBACK_STATUSES = ['pending', 'ready', 'in_progress', 'done', 'rejected'] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export const FEEDBACK_MOVES: Record<FeedbackStatus, readonly FeedbackStatus[]> = {
  pending: ['ready', 'rejected'],
  ready: ['in_progress', 'rejected'],
  in_progress: ['done', 'rejected'],
  done: [],
  rejected: []
};

export function canMoveFeedback(from: FeedbackStatus, to: FeedbackStatus): boolean {
  return FEEDBACK_MOVES[from]?.includes(to) ?? false;
}

/**
 * What the person allowed for this one report, chosen on the form each time.
 * `description`: the kind, the title, the description and the version, nothing
 * else. `details`: the screenshot, other images and the ticked context as
 * well. The validator and the Worker hold it against any client.
 */
export const FEEDBACK_CONSENTS = ['description', 'details'] as const;
export type FeedbackConsent = (typeof FEEDBACK_CONSENTS)[number];

export const FEEDBACK_KINDS = ['bug', 'improvement'] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

export const FEEDBACK_LIMITS = {
  title: 200,
  description: 20_000,
  /** The parts of context, and what each may hold: a report, not a dump. */
  parts: 12,
  partLabel: 80,
  partText: 60_000,
  contextChars: 200_000,
  images: 5,
  imageBytes: 8 * 1024 * 1024,
  imageName: 100
} as const;

/** What a whole request may weigh: every image at its ceiling, and the text. */
export const FEEDBACK_MAX_BODY = FEEDBACK_LIMITS.images * FEEDBACK_LIMITS.imageBytes + 1024 * 1024;

export interface FeedbackContextPart {
  id: string;
  label: string;
  text: string;
}

export interface FeedbackRequest {
  consent: FeedbackConsent;
  kind: FeedbackKind;
  title: string;
  description: string;
  appVersion: string;
  platform: string;
  parts: FeedbackContextPart[];
}

/** What the person is left with: the one thing they can quote to ask for a report's deletion. */
export const FEEDBACK_REFERENCE = /^AR-[0-9A-HJKMNP-TV-Z]{8}$/;

export interface FeedbackAnswer {
  id: string;
  reference: string;
}

const PART_ID = /^[a-z][a-z-]{0,31}$/;

function text(value: unknown, name: string, max: number): string {
  if (typeof value !== 'string') throw new Error(`${name} is missing.`);
  if (value.length > max) throw new Error(`${name} is too long: ${max.toLocaleString('en-US')} characters at most.`);
  return value;
}

/** The request as this side may store or send it; throws a sentence a person can read. */
export function parseFeedbackRequest(value: unknown): FeedbackRequest {
  if (typeof value !== 'object' || value === null) throw new Error('That is not a report.');
  const raw = value as Record<string, unknown>;
  const consent = FEEDBACK_CONSENTS.find((one) => one === raw.consent);
  if (!consent) throw new Error('A report says what its sender allowed.');
  const kind = FEEDBACK_KINDS.find((one) => one === raw.kind);
  if (!kind) throw new Error('A report is a bug or an improvement.');
  const title = text(raw.title, 'The title', FEEDBACK_LIMITS.title).trim();
  if (!title) throw new Error('The title is empty.');
  // Reports get no reply, so none carries an address (1022/no-reply-no-address).
  if ('contact' in raw) throw new Error('A report does not carry an address.');
  if (!Array.isArray(raw.parts) || raw.parts.length > FEEDBACK_LIMITS.parts) throw new Error('Too much context.');
  let chars = 0;
  const parts = raw.parts.map((part: unknown): FeedbackContextPart => {
    const one = (typeof part === 'object' && part !== null ? part : {}) as Record<string, unknown>;
    const id = text(one.id, 'A part of the context', 32);
    if (!PART_ID.test(id)) throw new Error('A part of the context has no usable name.');
    const body = text(one.text, 'A part of the context', FEEDBACK_LIMITS.partText);
    chars += body.length;
    return { id, label: text(one.label, 'A part of the context', FEEDBACK_LIMITS.partLabel), text: body };
  });
  if (chars > FEEDBACK_LIMITS.contextChars) throw new Error('Too much context.');
  const platform = text(raw.platform, 'The system', 40);
  if (consent === 'description' && (parts.length || platform)) {
    throw new Error('This report allowed its description only, so it cannot carry anything else.');
  }
  return {
    consent,
    kind,
    title,
    description: text(raw.description ?? '', 'The description', FEEDBACK_LIMITS.description),
    appVersion: text(raw.appVersion, 'The version', 40),
    platform,
    parts
  };
}

export type FeedbackImageType = 'image/png' | 'image/jpeg' | 'image/webp';

/** What a picture is by its first bytes; a name or a declared type proves nothing, and an SVG is a program. */
export function feedbackImageType(bytes: Uint8Array): FeedbackImageType | null {
  const starts = (...head: number[]): boolean => head.every((byte, i) => bytes[i] === byte);
  if (starts(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'image/png';
  if (starts(0xff, 0xd8, 0xff)) return 'image/jpeg';
  const riff = starts(0x52, 0x49, 0x46, 0x46);
  const webp = bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50;
  return riff && webp ? 'image/webp' : null;
}
